import { ContentfulLivePreview } from "@contentful/live-preview";
import {
  CtaReferenceContentAllowed,
  CtaReferenceLinkProps,
  CtaReferenceProps,
} from "../CtaReference/types";
import { Link } from "gatsby";
import { LiveInspectorData } from "types/types";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import ChildrenRender from "utilities/ChildrenRender";
import CtaModal from "components/CtaModal/CfCtaModal";
import React from "react";
import classNames from "classnames";

export const getUrlLink = ({
  node_locale,
  queryParams,
  anchor,
  ctaType,
  internalLink,
  internalStaticLink,
  externalLink,
}: CtaReferenceLinkProps) => {
  let linkUrl = "";
  const setQueryParameters = (link: string) =>
    `${link}${queryParams && queryParams !== "" ? queryParams : ""}`;
  const setAnchorId = (link: string) =>
    `${link}${anchor && anchor !== "" ? anchor : ""}`;

  switch (ctaType) {
    case "internalLink":
      linkUrl =
        internalLink && internalLink.node_locale
          ? `/${internalLink.node_locale}/${internalLink.slug}`
          : "";
      linkUrl = setQueryParameters(linkUrl);
      linkUrl = setAnchorId(linkUrl);
      break;
    case "internalStaticLink":
      linkUrl = `/${node_locale}${internalStaticLink}`;
      linkUrl = setQueryParameters(linkUrl);
      linkUrl = setAnchorId(linkUrl);
      break;
    case "internalStaticAutogenLink":
      linkUrl = `${internalStaticLink}`;
      break;
    case "externalLink":
      linkUrl = externalLink || "";
      linkUrl = setQueryParameters(linkUrl);
      break;
    default:
      return "";
  }
  return linkUrl;
};

const CtaReference = <T extends CtaReferenceContentAllowed>({
  children,
  ...props
}: CtaReferenceProps<T>) => {
  const liveProps = useContentfulLiveUpdates({
    ...props,
    sys: {
      id: props.contentful_id,
    },
  });

  const {
    ctaType,
    internalLink,
    internalStaticLink,
    externalLink,
    anchor,
    targetBlank,
    queryParams,
    modal,
    customClass,
    customEvent,
    className,
    onClick,
    node_locale,
    dataTracking,
    dataPosition,
  } = liveProps;

  const customEventClass =
    customEvent !== undefined && customEvent !== null && customEvent.length
      ? customEvent.map((e) => e.value)
      : null;

  const classLink = classNames(
    className,
    liveProps?.customClass || customClass,
    customEventClass,
  );

  let linkUrl = "";

  const setQueryParameters = (link: string) =>
    `${link}${queryParams && queryParams !== "" ? queryParams : ""}`;
  const setAnchorId = (link: string) =>
    `${link}${anchor && anchor !== "" ? anchor : ""}`;

  const childrenRender = () => {
    if (
      typeof children.type === "string" ||
      children.props?.typename === "ContentfulCtaContentV2"
    ) {
      return <ChildrenRender>{children}</ChildrenRender>;
    }
    return (
      <ChildrenRender propsToChild={{ hasParentLink: true }}>
        {children}
      </ChildrenRender>
    );
  };

  const liveInspector: LiveInspectorData = {
    cta: ContentfulLivePreview.getProps({
      entryId: props.contentful_id || "",
      fieldId: "entryTitle",
    }),
  };

  return (
    <>
      {(() => {
        switch (ctaType) {
          case "internalLink":
            if (internalLink === undefined) return null;
            linkUrl =
              internalLink?.node_locale &&
              `/${internalLink.node_locale}/${internalLink.slug}`;
            linkUrl = setQueryParameters(linkUrl);
            linkUrl = setAnchorId(linkUrl);
            return (
              <Link
                className={classLink}
                activeClassName="active"
                partiallyActive
                to={linkUrl}
                onClick={onClick}
                data-tracking={dataTracking}
                data-position={dataPosition}
                {...liveInspector?.cta}
              >
                {childrenRender()}
              </Link>
            );
          case "internalStaticLink":
            linkUrl = `/${node_locale}${internalStaticLink}`;
            linkUrl = setQueryParameters(linkUrl);
            linkUrl = setAnchorId(linkUrl);
            return (
              <Link
                className={classLink}
                activeClassName="active"
                partiallyActive
                to={linkUrl}
                onClick={onClick}
                data-tracking={dataTracking}
                data-position={dataPosition}
                {...liveInspector?.cta}
              >
                {childrenRender()}
              </Link>
            );
          case "internalStaticAutogenLink":
            linkUrl = `${internalStaticLink}`;
            return (
              <Link
                className={classLink}
                activeClassName="active"
                partiallyActive
                to={linkUrl}
                onClick={onClick}
                data-tracking={dataTracking}
                data-position={dataPosition}
                {...liveInspector?.cta}
              >
                {childrenRender()}
              </Link>
            );
          case "externalLink":
            linkUrl = externalLink || "";
            linkUrl = setQueryParameters(linkUrl);
            return (
              <a
                className={classLink}
                href={linkUrl}
                onClick={onClick}
                target={targetBlank ? "_blank" : undefined}
                data-tracking={dataTracking}
                data-position={dataPosition}
                rel="noreferrer"
                {...liveInspector?.cta}
              >
                {/* {children} */}
                {childrenRender()}
              </a>
            );
          case "openModal":
            return (
              <CtaModal
                modal={modal}
                dataTracking={dataTracking}
                dataPosition={dataPosition}
                liveInspector={liveInspector}
              >
                {childrenRender()}
              </CtaModal>
            );
          default:
            return null;
        }
      })()}
    </>
  );
};

export default CtaReference;
